import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import {
  Mail, Settings, Send, FileText, History, Plus, Trash2, Edit,
  Loader2, CheckCircle2, XCircle, RefreshCw, AlertCircle, Key,
  Users, Eye, Activity, Code, Sparkles, Server, Lock,
  LayoutTemplate, Palette, Zap, ChevronRight, Copy
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ────────────────────────────────────────────────
// PROFESSIONAL SAMPLE TEMPLATES
// ────────────────────────────────────────────────
const SAMPLE_TEMPLATES = [
  {
    name: 'Bienvenida Premium',
    category: 'transactional',
    description: 'Email de bienvenida para nuevos restaurantes',
    thumbnail_color: '#f97316',
    subject: 'Bienvenido a OkReserva — {{nombre_restaurante}}',
    html: `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Bienvenido a OkReserva</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; background: #f0f2f5; }
  .wrapper { max-width: 620px; margin: 0 auto; padding: 20px 0; }
  .header { background: linear-gradient(135deg, #f97316 0%, #ea580c 100%); padding: 48px 40px; text-align: center; border-radius: 12px 12px 0 0; }
  .header img { width: 48px; height: 48px; margin-bottom: 16px; }
  .header h1 { color: #fff; font-size: 28px; font-weight: 700; letter-spacing: -0.5px; }
  .header p { color: rgba(255,255,255,0.85); font-size: 16px; margin-top: 8px; }
  .body { background: #fff; padding: 40px; }
  .body h2 { font-size: 22px; color: #111827; font-weight: 600; margin-bottom: 16px; }
  .body p { color: #4b5563; font-size: 15px; line-height: 1.7; margin-bottom: 16px; }
  .feature-list { background: #fff7ed; border-radius: 8px; padding: 20px 24px; margin: 24px 0; }
  .feature-item { display: flex; align-items: center; gap: 12px; padding: 8px 0; color: #374151; font-size: 14px; border-bottom: 1px solid #fed7aa; }
  .feature-item:last-child { border-bottom: none; }
  .feature-icon { width: 20px; height: 20px; color: #f97316; flex-shrink: 0; }
  .cta { text-align: center; margin: 32px 0; }
  .cta a { background: linear-gradient(135deg, #f97316, #ea580c); color: #fff; text-decoration: none; padding: 14px 36px; border-radius: 8px; font-size: 16px; font-weight: 600; display: inline-block; }
  .divider { border: none; border-top: 1px solid #e5e7eb; margin: 24px 0; }
  .footer { background: #111827; padding: 28px 40px; border-radius: 0 0 12px 12px; text-align: center; }
  .footer p { color: #9ca3af; font-size: 13px; line-height: 1.6; }
  .footer a { color: #f97316; text-decoration: none; }
  .footer .social { margin: 12px 0; }
  .footer .social a { color: #6b7280; margin: 0 8px; font-size: 12px; text-decoration: none; }
</style>
</head>
<body>
<div class="wrapper">
  <div class="header">
    <h1>OkReserva</h1>
    <p>Sistema de Reservaciones Inteligente</p>
  </div>
  <div class="body">
    <h2>¡Bienvenido, {{nombre_propietario}}! 🎉</h2>
    <p>Tu restaurante <strong>{{nombre_restaurante}}</strong> ya está en OkReserva. A partir de hoy, gestiona tus reservaciones de forma inteligente y profesional.</p>
    <div class="feature-list">
      <div class="feature-item">✅ &nbsp; Reservaciones en línea 24/7</div>
      <div class="feature-item">✅ &nbsp; Recordatorios automáticos por WhatsApp</div>
      <div class="feature-item">✅ &nbsp; Analíticas en tiempo real</div>
      <div class="feature-item">✅ &nbsp; OkWallet — Depósitos digitales</div>
      <div class="feature-item">✅ &nbsp; Agente de voz IA para atender llamadas</div>
    </div>
    <p>Para comenzar, accede a tu panel con las credenciales que recibiste durante el registro.</p>
    <div class="cta">
      <a href="https://app.okreserva.com">Acceder a mi Panel →</a>
    </div>
    <hr class="divider">
    <p style="font-size:13px; color:#9ca3af;">¿Tienes dudas? Escríbenos a <a href="mailto:soporte@okreserva.com" style="color:#f97316;">soporte@okreserva.com</a></p>
  </div>
  <div class="footer">
    <p>OkReserva · La plataforma de reservaciones para restaurantes</p>
    <div class="social">
      <a href="#">Instagram</a> · <a href="#">WhatsApp</a> · <a href="#">Web</a>
    </div>
    <p style="margin-top:12px; font-size:11px;">
      Recibiste este correo porque te registraste en OkReserva.<br>
      <a href="{{unsubscribe_link}}">Cancelar suscripción</a>
    </p>
  </div>
</div>
</body>
</html>`
  },
  {
    name: 'Confirmación de Reserva',
    category: 'transactional',
    description: 'Confirmación automática para el comensal',
    thumbnail_color: '#10b981',
    subject: 'Tu reserva en {{nombre_restaurante}} está confirmada ✓',
    html: `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Reserva Confirmada</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; background: #f0fdf4; }
  .wrapper { max-width: 580px; margin: 0 auto; padding: 20px 0; }
  .header { background: #10b981; padding: 36px 40px; text-align: center; border-radius: 12px 12px 0 0; }
  .check-icon { width: 56px; height: 56px; background: rgba(255,255,255,0.2); border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; margin-bottom: 12px; font-size: 28px; }
  .header h1 { color: #fff; font-size: 24px; font-weight: 700; }
  .header p { color: rgba(255,255,255,0.9); font-size: 15px; margin-top: 6px; }
  .body { background: #fff; padding: 36px 40px; }
  .reservation-card { background: #f0fdf4; border: 2px solid #d1fae5; border-radius: 10px; padding: 24px; margin: 20px 0; }
  .detail-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 0; border-bottom: 1px solid #d1fae5; font-size: 14px; }
  .detail-row:last-child { border-bottom: none; }
  .detail-label { color: #6b7280; font-weight: 500; }
  .detail-value { color: #111827; font-weight: 600; }
  .confirm-code { background: #111827; color: #10b981; font-family: monospace; font-size: 22px; letter-spacing: 4px; padding: 14px; border-radius: 8px; text-align: center; margin: 20px 0; font-weight: 700; }
  .body p { color: #4b5563; font-size: 14px; line-height: 1.7; margin-bottom: 12px; }
  .footer { background: #f9fafb; padding: 24px 40px; border-radius: 0 0 12px 12px; text-align: center; }
  .footer p { color: #9ca3af; font-size: 12px; }
  .map-btn { display: inline-block; background: #fff; border: 1px solid #d1fae5; color: #059669; padding: 10px 24px; border-radius: 6px; text-decoration: none; font-size: 13px; font-weight: 500; margin-top: 8px; }
</style>
</head>
<body>
<div class="wrapper">
  <div class="header">
    <div class="check-icon">✓</div>
    <h1>¡Reserva Confirmada!</h1>
    <p>{{nombre_restaurante}}</p>
  </div>
  <div class="body">
    <p>Hola <strong>{{nombre_comensal}}</strong>, tu reserva está confirmada. Te esperamos.</p>
    <div class="reservation-card">
      <div class="detail-row"><span class="detail-label">📅 Fecha</span><span class="detail-value">{{fecha}}</span></div>
      <div class="detail-row"><span class="detail-label">⏰ Hora</span><span class="detail-value">{{hora}}</span></div>
      <div class="detail-row"><span class="detail-label">👥 Personas</span><span class="detail-value">{{personas}}</span></div>
      <div class="detail-row"><span class="detail-label">📍 Ubicación</span><span class="detail-value">{{direccion}}</span></div>
    </div>
    <p style="text-align:center; font-size:13px; color:#6b7280;">Código de confirmación</p>
    <div class="confirm-code">{{codigo_confirmacion}}</div>
    <p>Si necesitas modificar o cancelar tu reserva, contáctanos con al menos 2 horas de anticipación.</p>
    <div style="text-align:center; margin-top:16px;">
      <a class="map-btn" href="{{maps_link}}">📍 Ver en el mapa</a>
    </div>
  </div>
  <div class="footer">
    <p>{{nombre_restaurante}} · {{telefono_restaurante}}</p>
    <p style="margin-top:4px;">¿Dudas? <a href="tel:{{telefono_restaurante}}" style="color:#059669;">Llámanos</a></p>
  </div>
</div>
</body>
</html>`
  },
  {
    name: 'Campaña Marketing',
    category: 'marketing',
    description: 'Campaña promocional con oferta especial',
    thumbnail_color: '#8b5cf6',
    subject: '🎉 Oferta especial esta semana en {{nombre_restaurante}}',
    html: `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Oferta Especial</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; background: #faf5ff; }
  .wrapper { max-width: 600px; margin: 0 auto; padding: 20px 0; }
  .header { background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); padding: 48px 40px; text-align: center; border-radius: 12px 12px 0 0; position: relative; overflow: hidden; }
  .header::before { content: ''; position: absolute; top: -40px; right: -40px; width: 160px; height: 160px; background: rgba(255,255,255,0.08); border-radius: 50%; }
  .header::after { content: ''; position: absolute; bottom: -30px; left: -30px; width: 120px; height: 120px; background: rgba(255,255,255,0.06); border-radius: 50%; }
  .badge { background: rgba(255,255,255,0.2); color: #fff; font-size: 12px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; padding: 6px 16px; border-radius: 100px; display: inline-block; margin-bottom: 16px; }
  .header h1 { color: #fff; font-size: 32px; font-weight: 800; line-height: 1.2; }
  .header .discount { color: #fde68a; font-size: 48px; font-weight: 900; display: block; margin: 8px 0; }
  .body { background: #fff; padding: 40px; }
  .body p { color: #4b5563; font-size: 15px; line-height: 1.7; margin-bottom: 16px; }
  .promo-box { background: linear-gradient(135deg, #faf5ff, #ede9fe); border: 2px dashed #8b5cf6; border-radius: 10px; padding: 20px; text-align: center; margin: 24px 0; }
  .promo-code { font-family: monospace; font-size: 24px; font-weight: 800; color: #7c3aed; letter-spacing: 3px; }
  .promo-box p { color: #6b7280; font-size: 13px; margin: 6px 0 0; }
  .cta { text-align: center; margin: 28px 0; }
  .cta a { background: linear-gradient(135deg, #8b5cf6, #7c3aed); color: #fff; text-decoration: none; padding: 14px 40px; border-radius: 8px; font-size: 16px; font-weight: 700; display: inline-block; }
  .expire { text-align: center; color: #9ca3af; font-size: 12px; margin-top: 8px; }
  .divider { border: none; border-top: 1px solid #e5e7eb; margin: 24px 0; }
  .footer { background: #1e1b4b; padding: 28px 40px; border-radius: 0 0 12px 12px; text-align: center; }
  .footer p { color: #a5b4fc; font-size: 12px; line-height: 1.6; }
  .footer a { color: #c4b5fd; text-decoration: none; }
</style>
</head>
<body>
<div class="wrapper">
  <div class="header">
    <div class="badge">✨ Oferta Especial</div>
    <h1>Esta semana en<br>{{nombre_restaurante}}</h1>
    <span class="discount">{{descuento}}% OFF</span>
  </div>
  <div class="body">
    <p>¡Hola <strong>{{nombre_cliente}}</strong>! Tenemos una oferta exclusiva para ti por ser parte de nuestra comunidad.</p>
    <p>{{descripcion_oferta}}</p>
    <div class="promo-box">
      <div class="promo-code">{{codigo_promo}}</div>
      <p>Usa este código al hacer tu reservación</p>
    </div>
    <div class="cta">
      <a href="{{link_reservacion}}">Reservar ahora →</a>
    </div>
    <p class="expire">⏳ Oferta válida hasta el {{fecha_vencimiento}}</p>
    <hr class="divider">
    <p style="font-size:13px; color:#6b7280; text-align:center;">
      {{nombre_restaurante}} · {{direccion}} · <a href="tel:{{telefono}}" style="color:#8b5cf6;">{{telefono}}</a>
    </p>
  </div>
  <div class="footer">
    <p>Recibiste este correo porque eres cliente de {{nombre_restaurante}}.</p>
    <p style="margin-top:8px;"><a href="{{unsubscribe_link}}">Cancelar suscripción</a></p>
  </div>
</div>
</body>
</html>`
  },
  {
    name: 'Recordatorio de Reserva',
    category: 'transactional',
    description: 'Recordatorio 24h antes de la reserva',
    thumbnail_color: '#3b82f6',
    subject: '⏰ Recordatorio: Tu mesa en {{nombre_restaurante}} es mañana',
    html: `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Recordatorio de Reserva</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; background: #eff6ff; }
  .wrapper { max-width: 560px; margin: 0 auto; padding: 20px 0; }
  .header { background: #3b82f6; padding: 32px 36px; text-align: center; border-radius: 12px 12px 0 0; }
  .clock { font-size: 40px; margin-bottom: 8px; }
  .header h1 { color: #fff; font-size: 22px; font-weight: 700; }
  .body { background: #fff; padding: 32px 36px; }
  .countdown { background: #eff6ff; border-radius: 10px; padding: 20px; text-align: center; margin: 16px 0; }
  .countdown .time { font-size: 36px; font-weight: 800; color: #3b82f6; }
  .countdown p { color: #6b7280; font-size: 13px; margin-top: 4px; }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 20px 0; }
  .info-card { background: #f8fafc; border-radius: 8px; padding: 14px; text-align: center; }
  .info-card .icon { font-size: 20px; }
  .info-card .label { color: #9ca3af; font-size: 11px; margin-top: 4px; }
  .info-card .value { color: #111827; font-size: 15px; font-weight: 600; }
  .body p { color: #4b5563; font-size: 14px; line-height: 1.7; margin: 12px 0; }
  .action-row { display: flex; gap: 12px; justify-content: center; margin: 24px 0; }
  .btn-primary { background: #3b82f6; color: #fff; text-decoration: none; padding: 11px 24px; border-radius: 7px; font-size: 14px; font-weight: 600; }
  .btn-secondary { background: #fff; border: 1px solid #e5e7eb; color: #374151; text-decoration: none; padding: 11px 24px; border-radius: 7px; font-size: 14px; font-weight: 500; }
  .footer { background: #1e3a8a; padding: 24px 36px; border-radius: 0 0 12px 12px; text-align: center; }
  .footer p { color: #93c5fd; font-size: 12px; }
</style>
</head>
<body>
<div class="wrapper">
  <div class="header">
    <div class="clock">⏰</div>
    <h1>Tu mesa es mañana</h1>
  </div>
  <div class="body">
    <p>Hola <strong>{{nombre_comensal}}</strong>, solo un recordatorio:</p>
    <div class="countdown">
      <div class="time">{{horas_restantes}}h</div>
      <p>para tu reserva en {{nombre_restaurante}}</p>
    </div>
    <div class="info-grid">
      <div class="info-card"><div class="icon">📅</div><div class="label">FECHA</div><div class="value">{{fecha}}</div></div>
      <div class="info-card"><div class="icon">⏰</div><div class="label">HORA</div><div class="value">{{hora}}</div></div>
      <div class="info-card"><div class="icon">👥</div><div class="label">PERSONAS</div><div class="value">{{personas}}</div></div>
      <div class="info-card"><div class="icon">🪑</div><div class="label">MESA</div><div class="value">{{mesa}}</div></div>
    </div>
    <p style="text-align:center; color:#6b7280; font-size:13px;">¿Necesitas hacer algún cambio?</p>
    <div class="action-row">
      <a class="btn-primary" href="{{link_modificar}}">Modificar reserva</a>
      <a class="btn-secondary" href="{{link_cancelar}}">Cancelar</a>
    </div>
    <p style="font-size:12px; color:#9ca3af; text-align:center;">Política de cancelación: mínimo 2 horas antes</p>
  </div>
  <div class="footer">
    <p>{{nombre_restaurante}} · {{telefono}}</p>
  </div>
</div>
</body>
</html>`
  },
];

// ────────────────────────────────────────────────
// HTML TEMPLATE EDITOR COMPONENT
// ────────────────────────────────────────────────
const VARIABLES = [
  '{{nombre_restaurante}}', '{{nombre_propietario}}', '{{nombre_comensal}}',
  '{{nombre_cliente}}', '{{fecha}}', '{{hora}}', '{{personas}}',
  '{{codigo_confirmacion}}', '{{direccion}}', '{{telefono}}',
  '{{link_reservacion}}', '{{link_modificar}}', '{{link_cancelar}}',
  '{{descuento}}', '{{codigo_promo}}', '{{fecha_vencimiento}}',
  '{{unsubscribe_link}}', '{{maps_link}}',
];

function HtmlTemplateEditor({ value, onChange }) {
  const iframeRef = useRef(null);
  const [view, setView] = useState('split'); // 'code' | 'preview' | 'split'

  useEffect(() => {
    if (iframeRef.current) {
      const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
      doc.open();
      doc.write(value || '<p style="color:#9ca3af;font-family:sans-serif;padding:40px;text-align:center;">Vista previa aparecerá aquí...</p>');
      doc.close();
    }
  }, [value]);

  function insertVariable(v) {
    const el = document.getElementById('html-editor');
    if (!el) { onChange(value + v); return; }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const newVal = value.substring(0, start) + v + value.substring(end);
    onChange(newVal);
    setTimeout(() => { el.selectionStart = el.selectionEnd = start + v.length; el.focus(); }, 0);
  }

  return (
    <div className="border rounded-xl overflow-hidden bg-gray-50 dark:bg-gray-900">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 bg-white dark:bg-gray-800 border-b gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          <span className="text-xs font-medium text-gray-500 mr-2">Vista:</span>
          {[['split','Split'],['code','Código'],['preview','Preview']].map(([v,l]) => (
            <button key={v} onClick={() => setView(v)}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${view === v ? 'bg-orange-100 text-orange-600' : 'text-gray-500 hover:bg-gray-100'}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-xs text-gray-400 mr-1">Insertar:</span>
          {['{{nombre_comensal}}','{{fecha}}','{{hora}}','{{nombre_restaurante}}','{{link_reservacion}}'].map(v => (
            <button key={v} onClick={() => insertVariable(v)}
              className="px-1.5 py-0.5 bg-orange-50 text-orange-600 text-[10px] rounded border border-orange-200 hover:bg-orange-100 font-mono">
              {v.replace(/[{}]/g,'').split('_').slice(-1)[0]}
            </button>
          ))}
          <Select onValueChange={insertVariable}>
            <SelectTrigger className="h-6 w-28 text-xs border-orange-200">
              <SelectValue placeholder="+ var" />
            </SelectTrigger>
            <SelectContent className="max-h-48">
              {VARIABLES.map(v => <SelectItem key={v} value={v} className="font-mono text-xs">{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Editor area */}
      <div className={`flex ${view === 'split' ? 'h-[420px]' : 'h-[480px]'}`}>
        {/* Code pane */}
        {(view === 'code' || view === 'split') && (
          <div className={`flex flex-col ${view === 'split' ? 'w-1/2 border-r' : 'w-full'}`}>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 text-xs text-gray-400 border-b border-gray-700">
              <Code className="h-3.5 w-3.5" />HTML
            </div>
            <textarea
              id="html-editor"
              value={value}
              onChange={e => onChange(e.target.value)}
              className="flex-1 bg-gray-900 text-green-300 font-mono text-xs p-4 resize-none border-0 outline-none leading-relaxed"
              spellCheck={false}
              placeholder="Escribe tu HTML aquí..."
            />
          </div>
        )}
        {/* Preview pane */}
        {(view === 'preview' || view === 'split') && (
          <div className={`flex flex-col ${view === 'split' ? 'w-1/2' : 'w-full'}`}>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white border-b text-xs text-gray-500">
              <Eye className="h-3.5 w-3.5" />Vista Previa
            </div>
            <iframe
              ref={iframeRef}
              title="preview"
              className="flex-1 bg-white"
              sandbox="allow-same-origin"
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────
// MAIN COMPONENT
// ────────────────────────────────────────────────
export default function AdminEmailServer() {
  const [tab, setTab] = useState('config');

  // Config state
  const [form, setForm] = useState({ client_id: '', client_secret: '', sender_name: 'OkReserva', sender_email: 'reservas@okreserva.com', reply_to: '', notes: '' });
  const [configInfo, setConfigInfo] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // SMTP state
  const [smtpForm, setSmtpForm] = useState({ username: '', password: '', port: 465, use_ssl: true, from_name: 'OkReserva', from_email: 'reservas@okreserva.com' });
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState(null);

  // Send email state
  const [sendForm, setSendForm] = useState({ to_emails: '', subject: '', html: SAMPLE_TEMPLATES[0].html, use_smtp: true });
  const [sending, setSending] = useState(false);

  // Bulk state
  const [bulkForm, setBulkForm] = useState({ subject: '', html: '', from_name: 'OkReserva', restaurant_ids: '', use_smtp: true });
  const [bulkSending, setBulkSending] = useState(false);

  // Templates state
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: '', subject: '', html: SAMPLE_TEMPLATES[0].html, category: 'transactional', description: '', thumbnail_color: '#f97316' });
  const [showSamplePicker, setShowSamplePicker] = useState(false);

  // Logs state
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const adminToken = localStorage.getItem('admin_token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` };

  useEffect(() => { loadConfig(); }, []);

  async function loadConfig() {
    try {
      const r = await fetch(`${API_URL}/api/email-server/admin/config`, { headers });
      if (r.ok) {
        const d = await r.json();
        setConfigInfo(d);
        setForm(f => ({
          ...f,
          sender_name: d.sender_name || 'OkReserva',
          sender_email: d.sender_email || '',
          reply_to: d.reply_to || '',
          notes: d.notes || '',
        }));
        setSmtpForm(f => ({
          ...f,
          port: d.smtp_port || 465,
          use_ssl: d.smtp_use_ssl !== false,
          from_name: d.smtp_from_name || 'OkReserva',
          from_email: d.smtp_from_email || '',
        }));
      }
    } catch {}
  }

  async function saveConfig() {
    if (!form.client_id || !form.client_secret) { toast.error('Client ID y Secret son requeridos'); return; }
    setConfigLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/email-server/admin/config`, { method: 'POST', headers, body: JSON.stringify(form) });
      const d = await r.json();
      if (d.ok) { toast.success(d.message); setForm(f => ({ ...f, client_id: '', client_secret: '' })); loadConfig(); }
      else toast.error(d.detail || 'Error al guardar');
    } catch { toast.error('Error de conexión'); }
    finally { setConfigLoading(false); }
  }

  async function testConnection() {
    setTesting(true); setTestResult(null);
    try {
      const r = await fetch(`${API_URL}/api/email-server/admin/test-connection`, { method: 'POST', headers });
      const d = await r.json();
      setTestResult(d);
      if (d.ok) toast.success(d.message); else toast.error(d.message);
    } catch { toast.error('Error de conexión'); }
    finally { setTesting(false); }
  }

  async function saveSmtpConfig() {
    if (!smtpForm.username || !smtpForm.password) { toast.error('Usuario y contraseña SMTP requeridos'); return; }
    setSmtpSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/email-server/admin/smtp-config`, { method: 'POST', headers, body: JSON.stringify(smtpForm) });
      const d = await r.json();
      if (d.ok) { toast.success(d.message); loadConfig(); } else toast.error(d.detail || 'Error');
    } catch { toast.error('Error de conexión'); }
    finally { setSmtpSaving(false); }
  }

  async function testSmtp() {
    setSmtpTesting(true); setSmtpTestResult(null);
    try {
      const r = await fetch(`${API_URL}/api/email-server/admin/test-smtp`, { method: 'POST', headers });
      const d = await r.json();
      setSmtpTestResult(d);
      if (d.ok) toast.success(d.message); else toast.error(d.message);
    } catch { toast.error('Error de conexión'); }
    finally { setSmtpTesting(false); }
  }

  async function sendDirectEmail() {
    const emails = sendForm.to_emails.split(/[\n,;]/).map(e => e.trim()).filter(Boolean);
    if (emails.length === 0) { toast.error('Agrega al menos un destinatario'); return; }
    if (!sendForm.subject) { toast.error('El asunto es requerido'); return; }
    setSending(true);
    try {
      const r = await fetch(`${API_URL}/api/email-server/admin/send-email`, {
        method: 'POST', headers,
        body: JSON.stringify({ to_emails: emails, subject: sendForm.subject, html: sendForm.html, use_smtp: sendForm.use_smtp }),
      });
      const d = await r.json();
      if (d.ok) { toast.success(d.message); setSendForm(f => ({ ...f, to_emails: '', subject: '' })); }
      else toast.error(d.detail || 'Error al enviar');
    } catch { toast.error('Error de conexión'); }
    finally { setSending(false); }
  }

  async function sendBulkEmail() {
    if (!bulkForm.subject || !bulkForm.html) { toast.error('Asunto y contenido requeridos'); return; }
    setBulkSending(true);
    try {
      const payload = { subject: bulkForm.subject, html: bulkForm.html, from_name: bulkForm.from_name, use_smtp: bulkForm.use_smtp };
      if (bulkForm.restaurant_ids.trim()) payload.restaurant_ids = bulkForm.restaurant_ids.split(/[\n,;]/).map(s => s.trim()).filter(Boolean);
      const r = await fetch(`${API_URL}/api/email-server/admin/bulk-email`, { method: 'POST', headers, body: JSON.stringify(payload) });
      const d = await r.json();
      if (d.ok) toast.success(d.message); else toast.error(d.detail || 'Error al enviar masivo');
    } catch { toast.error('Error de conexión'); }
    finally { setBulkSending(false); }
  }

  async function loadTemplates() {
    setTemplatesLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/email-server/admin/templates`, { headers });
      if (r.ok) { const d = await r.json(); setTemplates(d.templates || []); }
    } catch {}
    finally { setTemplatesLoading(false); }
  }

  async function saveTemplate() {
    if (!templateForm.name || !templateForm.subject) { toast.error('Nombre y asunto requeridos'); return; }
    const url = editingTemplate
      ? `${API_URL}/api/email-server/admin/templates/${editingTemplate.id}`
      : `${API_URL}/api/email-server/admin/templates`;
    const method = editingTemplate ? 'PUT' : 'POST';
    try {
      const r = await fetch(url, { method, headers, body: JSON.stringify(templateForm) });
      const d = await r.json();
      if (d.ok || d.id) {
        toast.success(editingTemplate ? 'Plantilla actualizada' : 'Plantilla creada');
        setShowTemplateEditor(false);
        loadTemplates();
      } else toast.error(d.detail || 'Error');
    } catch { toast.error('Error de conexión'); }
  }

  async function deleteTemplate(id) {
    if (!window.confirm('¿Eliminar esta plantilla?')) return;
    try {
      await fetch(`${API_URL}/api/email-server/admin/templates/${id}`, { method: 'DELETE', headers });
      toast.success('Plantilla eliminada'); loadTemplates();
    } catch { toast.error('Error'); }
  }

  async function loadLogs() {
    setLogsLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/email-server/admin/logs?limit=50`, { headers });
      if (r.ok) { const d = await r.json(); setLogs(d.logs || []); }
    } catch {}
    finally { setLogsLoading(false); }
  }

  function openCreateTemplate(sample) {
    setEditingTemplate(null);
    setTemplateForm(sample
      ? { name: sample.name, subject: sample.subject, html: sample.html, category: sample.category, description: sample.description, thumbnail_color: sample.thumbnail_color }
      : { name: '', subject: '', html: '', category: 'transactional', description: '', thumbnail_color: '#f97316' }
    );
    setShowSamplePicker(false);
    setShowTemplateEditor(true);
  }

  function openEditTemplate(t) {
    setEditingTemplate(t);
    setTemplateForm({ name: t.name, subject: t.subject, html: t.html, category: t.category, description: t.description || '', thumbnail_color: t.thumbnail_color || '#f97316' });
    setShowTemplateEditor(true);
  }

  const smtpConfigured = configInfo?.smtp_configured;
  const apiConfigured = configInfo?.configured;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Email Server — SendPulse</h1>
        <p className="text-sm text-gray-500 mt-1">Configura el servidor de correo para toda la plataforma OkReserva (REST API + SMTP).</p>
      </div>

      <Tabs value={tab} onValueChange={v => { setTab(v); if (v === 'templates' && templates.length === 0) loadTemplates(); if (v === 'logs') loadLogs(); }}>
        <TabsList className="bg-gray-100 flex-wrap h-auto gap-0.5">
          <TabsTrigger value="config"><Key className="h-4 w-4 mr-1.5" />API SendPulse</TabsTrigger>
          <TabsTrigger value="smtp"><Server className="h-4 w-4 mr-1.5" />SMTP</TabsTrigger>
          <TabsTrigger value="send"><Send className="h-4 w-4 mr-1.5" />Enviar</TabsTrigger>
          <TabsTrigger value="bulk"><Users className="h-4 w-4 mr-1.5" />Masivo</TabsTrigger>
          <TabsTrigger value="templates"><LayoutTemplate className="h-4 w-4 mr-1.5" />Plantillas</TabsTrigger>
          <TabsTrigger value="logs"><History className="h-4 w-4 mr-1.5" />Historial</TabsTrigger>
        </TabsList>

        {/* ── API CONFIG ── */}
        <TabsContent value="config" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Key className="h-5 w-5 text-orange-500" />Credenciales SendPulse (REST API)</CardTitle>
              <CardDescription>El OAuth2 REST API se usa para enviar emails vía SendPulse directamente. Los credentials se guardan en DB, no en variables de entorno.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {configInfo && (
                <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg flex-wrap ${apiConfigured ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
                  {apiConfigured ? <CheckCircle2 className="h-4 w-4 flex-shrink-0" /> : <AlertCircle className="h-4 w-4 flex-shrink-0" />}
                  <span>{apiConfigured ? `✓ REST API configurado — ${configInfo.client_id_masked}` : 'REST API no configurado'}</span>
                  {configInfo.last_test && (
                    <span className="ml-auto text-xs opacity-70">
                      Prueba: {new Date(configInfo.last_test).toLocaleString('es-MX')} — {configInfo.last_test_status === 'success' ? '✓ OK' : '✗ Error'}
                    </span>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client ID <span className="text-red-400">*</span></Label>
                  <Input type="password" value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))} placeholder="sp_id_xxxxxx" />
                </div>
                <div className="space-y-2">
                  <Label>Client Secret <span className="text-red-400">*</span></Label>
                  <Input type="password" value={form.client_secret} onChange={e => setForm(f => ({ ...f, client_secret: e.target.value }))} placeholder="sp_sk_xxxxxx" />
                </div>
                <div className="space-y-2">
                  <Label>Nombre del remitente</Label>
                  <Input value={form.sender_name} onChange={e => setForm(f => ({ ...f, sender_name: e.target.value }))} placeholder="OkReserva" />
                </div>
                <div className="space-y-2">
                  <Label>Email del remitente</Label>
                  <Input type="email" value={form.sender_email} onChange={e => setForm(f => ({ ...f, sender_email: e.target.value }))} />
                </div>
              </div>
              <div className="flex gap-3 flex-wrap">
                <Button onClick={saveConfig} disabled={configLoading} className="bg-orange-500 hover:bg-orange-600">
                  {configLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Guardar
                </Button>
                <Button variant="outline" onClick={testConnection} disabled={testing}>
                  {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Activity className="h-4 w-4 mr-2" />}Probar Conexión
                </Button>
              </div>
              {testResult && (
                <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${testResult.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {testResult.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}{testResult.message}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── SMTP CONFIG ── */}
        <TabsContent value="smtp" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Server className="h-5 w-5 text-orange-500" />Configuración SMTP — SendPulse</CardTitle>
              <CardDescription>
                El SMTP es el método principal y más confiable para enviar emails. Usa las credenciales SMTP de tu cuenta SendPulse.<br />
                <span className="text-orange-500 font-medium">Host: smtp.sendpulse.com · Puerto SSL: 465 · Puerto TLS: 587</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {configInfo && (
                <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${smtpConfigured ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700'}`}>
                  {smtpConfigured ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                  {smtpConfigured ? `SMTP configurado — usuario: ${configInfo.smtp_username_masked}` : 'SMTP no configurado. Configura para usar como servidor principal.'}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Usuario SMTP (API User ID) <span className="text-red-400">*</span></Label>
                  <Input type="password" value={smtpForm.username} onChange={e => setSmtpForm(f => ({ ...f, username: e.target.value }))} placeholder="Tu API User ID de SendPulse" />
                </div>
                <div className="space-y-2">
                  <Label>Contraseña SMTP (API Secret) <span className="text-red-400">*</span></Label>
                  <Input type="password" value={smtpForm.password} onChange={e => setSmtpForm(f => ({ ...f, password: e.target.value }))} placeholder="Tu API Secret de SendPulse" />
                </div>
                <div className="space-y-2">
                  <Label>Puerto</Label>
                  <Select value={String(smtpForm.port)} onValueChange={v => setSmtpForm(f => ({ ...f, port: parseInt(v), use_ssl: v === '465' }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="465">465 — SSL (recomendado)</SelectItem>
                      <SelectItem value="587">587 — STARTTLS</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Nombre del remitente</Label>
                  <Input value={smtpForm.from_name} onChange={e => setSmtpForm(f => ({ ...f, from_name: e.target.value }))} />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Email del remitente</Label>
                  <Input type="email" value={smtpForm.from_email} onChange={e => setSmtpForm(f => ({ ...f, from_email: e.target.value }))} placeholder="reservas@okreserva.com" />
                </div>
              </div>
              <div className="flex gap-3 flex-wrap">
                <Button onClick={saveSmtpConfig} disabled={smtpSaving} className="bg-orange-500 hover:bg-orange-600">
                  {smtpSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}Guardar SMTP
                </Button>
                <Button variant="outline" onClick={testSmtp} disabled={smtpTesting}>
                  {smtpTesting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Activity className="h-4 w-4 mr-2" />}Probar SMTP
                </Button>
              </div>
              {smtpTestResult && (
                <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${smtpTestResult.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {smtpTestResult.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}{smtpTestResult.message}
                </div>
              )}
              <div className="bg-blue-50 rounded-lg p-4 mt-2">
                <p className="text-sm font-medium text-blue-800 mb-2">¿Dónde encuentro las credenciales SMTP en SendPulse?</p>
                <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
                  <li>Inicia sesión en <strong>app.sendpulse.com</strong></li>
                  <li>Ve a <strong>Cuenta → API</strong></li>
                  <li>Usa el <strong>ID de usuario de la API</strong> como usuario SMTP</li>
                  <li>Usa la <strong>Clave secreta de la API</strong> como contraseña</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── SEND EMAIL ── */}
        <TabsContent value="send" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-orange-500" />Enviar Email Directo</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg text-sm">
                <span className="text-gray-600">Método de envío:</span>
                <div className="flex items-center gap-2">
                  <Switch checked={sendForm.use_smtp} onCheckedChange={v => setSendForm(f => ({ ...f, use_smtp: v }))} />
                  <span className={sendForm.use_smtp ? 'text-orange-600 font-medium' : 'text-gray-500'}>
                    {sendForm.use_smtp ? 'SMTP (recomendado)' : 'REST API'}
                  </span>
                  {!smtpConfigured && sendForm.use_smtp && <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">SMTP no configurado</Badge>}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Destinatarios (separados por línea o coma)</Label>
                <Textarea value={sendForm.to_emails} onChange={e => setSendForm(f => ({ ...f, to_emails: e.target.value }))} rows={3} placeholder="email@ejemplo.com&#10;otro@ejemplo.com" />
              </div>
              <div className="space-y-2">
                <Label>Asunto</Label>
                <Input value={sendForm.subject} onChange={e => setSendForm(f => ({ ...f, subject: e.target.value }))} placeholder="Asunto del correo" />
              </div>
              <div className="space-y-2">
                <Label>Contenido HTML</Label>
                <HtmlTemplateEditor value={sendForm.html} onChange={v => setSendForm(f => ({ ...f, html: v }))} />
              </div>
              <Button onClick={sendDirectEmail} disabled={sending} className="bg-orange-500 hover:bg-orange-600">
                {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}Enviar
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── BULK EMAIL ── */}
        <TabsContent value="bulk" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-orange-500" />Email Masivo a Restaurantes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg text-sm">
                <span className="text-gray-600">Método:</span>
                <div className="flex items-center gap-2">
                  <Switch checked={bulkForm.use_smtp} onCheckedChange={v => setBulkForm(f => ({ ...f, use_smtp: v }))} />
                  <span className={bulkForm.use_smtp ? 'text-orange-600 font-medium' : 'text-gray-500'}>
                    {bulkForm.use_smtp ? 'SMTP' : 'REST API'}
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>IDs de Restaurantes (vacío = todos)</Label>
                <Textarea value={bulkForm.restaurant_ids} onChange={e => setBulkForm(f => ({ ...f, restaurant_ids: e.target.value }))} rows={2} placeholder="Dejar vacío para enviar a todos..." />
              </div>
              <div className="space-y-2">
                <Label>Asunto</Label>
                <Input value={bulkForm.subject} onChange={e => setBulkForm(f => ({ ...f, subject: e.target.value }))} placeholder="Actualización importante de OkReserva" />
              </div>
              <div className="space-y-2">
                <Label>Contenido HTML</Label>
                <HtmlTemplateEditor value={bulkForm.html} onChange={v => setBulkForm(f => ({ ...f, html: v }))} />
              </div>
              <Button onClick={sendBulkEmail} disabled={bulkSending} className="bg-orange-500 hover:bg-orange-600">
                {bulkSending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}Enviar a Restaurantes
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── TEMPLATES ── */}
        <TabsContent value="templates" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">{templates.length} plantilla(s) guardada(s)</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={loadTemplates} disabled={templatesLoading}>
                <RefreshCw className={`h-4 w-4 mr-1.5 ${templatesLoading ? 'animate-spin' : ''}`} />Actualizar
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowSamplePicker(true)}>
                <Sparkles className="h-4 w-4 mr-1.5" />Desde muestra
              </Button>
              <Button size="sm" className="bg-orange-500 hover:bg-orange-600" onClick={() => openCreateTemplate(null)}>
                <Plus className="h-4 w-4 mr-1.5" />Nueva Plantilla
              </Button>
            </div>
          </div>

          {templatesLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
          ) : templates.length === 0 ? (
            <Card className="border-dashed border-2">
              <CardContent className="py-12 text-center">
                <LayoutTemplate className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">Sin plantillas guardadas</p>
                <p className="text-sm text-gray-400 mt-1">Crea plantillas profesionales para reutilizarlas en campañas.</p>
                <Button variant="outline" size="sm" className="mt-4" onClick={() => setShowSamplePicker(true)}>
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" />Ver plantillas de muestra
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {templates.map(t => (
                <Card key={t.id} className="overflow-hidden">
                  <div className="h-2" style={{ background: t.thumbnail_color || '#f97316' }} />
                  <CardHeader className="pb-2 pt-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <CardTitle className="text-sm truncate">{t.name}</CardTitle>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">{t.subject}</p>
                      </div>
                      <Badge variant="outline" className="text-xs capitalize flex-shrink-0">{t.category}</Badge>
                    </div>
                    {t.description && <p className="text-xs text-gray-400 mt-1">{t.description}</p>}
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex gap-1.5 flex-wrap">
                      <Button variant="outline" size="sm" className="h-7 text-xs flex-1" onClick={() => openEditTemplate(t)}>
                        <Edit className="h-3 w-3 mr-1" />Editar
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:bg-red-50" onClick={() => deleteTemplate(t.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── LOGS ── */}
        <TabsContent value="logs" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">{logs.length} registro(s)</p>
            <Button variant="outline" size="sm" onClick={loadLogs} disabled={logsLoading}>
              <RefreshCw className={`h-4 w-4 mr-1.5 ${logsLoading ? 'animate-spin' : ''}`} />Actualizar
            </Button>
          </div>
          {logsLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
          ) : (
            <Card>
              {logs.length === 0
                ? <CardContent className="py-12 text-center"><History className="h-12 w-12 text-gray-200 mx-auto mb-3" /><p className="text-gray-400">Sin envíos registrados</p></CardContent>
                : <div className="divide-y">{logs.map(log => (
                  <div key={log.id} className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-orange-50 flex items-center justify-center flex-shrink-0">
                        <Mail className="h-4 w-4 text-orange-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{log.subject || 'Sin asunto'}</p>
                        <p className="text-xs text-gray-500">
                          {log.type === 'bulk' ? `Masivo — ${log.sent_count}/${log.to_count}` : `Directo`} · {log.method?.toUpperCase() || 'API'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-xs font-medium ${log.status === 'sent' ? 'text-green-600' : 'text-red-500'}`}>{log.status === 'sent' ? '✓ Enviado' : 'Error'}</p>
                      <p className="text-xs text-gray-400">{new Date(log.created_at).toLocaleString('es-MX')}</p>
                    </div>
                  </div>
                ))}</div>
              }
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* ── SAMPLE PICKER DIALOG ── */}
      <Dialog open={showSamplePicker} onOpenChange={setShowSamplePicker}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-orange-500" />Plantillas de Muestra Profesionales</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            {SAMPLE_TEMPLATES.map(s => (
              <div key={s.name} className="border rounded-xl overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => openCreateTemplate(s)}>
                <div className="h-2" style={{ background: s.thumbnail_color }} />
                <div className="p-4">
                  <p className="font-medium text-gray-900 text-sm">{s.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{s.description}</p>
                  <div className="flex items-center justify-between mt-3">
                    <Badge variant="outline" className="text-xs capitalize">{s.category}</Badge>
                    <span className="text-xs text-orange-500 font-medium">Usar esta →</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── TEMPLATE EDITOR DIALOG ── */}
      <Dialog open={showTemplateEditor} onOpenChange={setShowTemplateEditor}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Editar Plantilla' : 'Nueva Plantilla'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="space-y-1 col-span-2">
                <Label>Nombre *</Label>
                <Input value={templateForm.name} onChange={e => setTemplateForm(f => ({ ...f, name: e.target.value }))} placeholder="Nombre de la plantilla" />
              </div>
              <div className="space-y-1">
                <Label>Categoría</Label>
                <Select value={templateForm.category} onValueChange={v => setTemplateForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="transactional">Transaccional</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="notification">Notificación</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Color</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={templateForm.thumbnail_color} onChange={e => setTemplateForm(f => ({ ...f, thumbnail_color: e.target.value }))}
                    className="h-9 w-14 rounded border cursor-pointer" />
                  <div className="h-9 flex-1 rounded border" style={{ background: templateForm.thumbnail_color }} />
                </div>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Asunto *</Label>
              <Input value={templateForm.subject} onChange={e => setTemplateForm(f => ({ ...f, subject: e.target.value }))} placeholder="Asunto del email con {{variables}}" />
            </div>
            <div className="space-y-1">
              <Label>Descripción (interna)</Label>
              <Input value={templateForm.description} onChange={e => setTemplateForm(f => ({ ...f, description: e.target.value }))} placeholder="Para qué se usa esta plantilla..." />
            </div>
            <div className="space-y-1">
              <Label>HTML del Email</Label>
              <HtmlTemplateEditor value={templateForm.html} onChange={v => setTemplateForm(f => ({ ...f, html: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateEditor(false)}>Cancelar</Button>
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={saveTemplate}>
              {editingTemplate ? 'Guardar Cambios' : 'Crear Plantilla'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
